import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { FieldPath, useFormContext } from 'react-hook-form';
import { IncomeVerificationType } from '@brighte/validation';
import { css } from '@emotion/css';
import { useLaunchDarklyContextUpdate } from '@hooks';
import { Box } from '@spark-web/box';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { INCOME_VERIFICATION } from '../../utils';
import BankStatementVerification from './BankStatementVerification';
import BankStatements from './BankStatements';
import UploadDocuments, { type VerificationStatus } from './UploadDocuments';

type IncomeOptionProps = {
  name: FieldPath<IncomeVerificationType>;
  isOpen: boolean;
  onToggle: () => void;
  financialProductId: string;
  vendorId: string;
  incomeType?: 'primary' | 'additional';
  onVerificationFailed?: (wasAlreadyFailed: boolean) => void;
};

export enum IncomeVerificationState {
  BANK_STATEMENT = 'BANK_STATEMENT',
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  BANK_STATEMENT_VERIFICATION = 'BANK_STATEMENT_VERIFICATION',
}

function IncomeVerificationOptions({
  name,
  isOpen,
  onToggle,
  financialProductId,
  vendorId,
  incomeType,
  onVerificationFailed,
}: IncomeOptionProps) {
  const [currentState, setCurrentState] = useState<IncomeVerificationState>(IncomeVerificationState.BANK_STATEMENT);
  const [isBankError, setIsBankError] = useState<boolean>(false);
  const [isDialogBusy, setIsDialogBusy] = useState(false);
  const [uploadVerificationStatus, setUploadVerificationStatus] = useState<VerificationStatus>('idle');
  const [verificationSessionFailureCount, setVerificationSessionFailureCount] = useState(0);
  const { clearErrors } = useFormContext<IncomeVerificationType>();

  const flags = useFlags();
  const updateLDContext = useLaunchDarklyContextUpdate();

  useEffect(() => {
    if (financialProductId && vendorId) {
      updateLDContext({
        financialProduct: { key: financialProductId },
        vendorId: { key: vendorId },
      });
    }
  }, [financialProductId, vendorId]);

  const isBankStatementEnabled = flags['bank-statements'] ?? true;
  const isFeedbackFlow = incomeType === 'primary';

  useEffect(() => {
    setCurrentState(IncomeVerificationState.BANK_STATEMENT);
    setUploadVerificationStatus('idle');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsDialogBusy(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (uploadVerificationStatus === 'failed') {
      setVerificationSessionFailureCount(prev => prev + 1);
    }
  }, [uploadVerificationStatus]);

  const { VERIFY_PRIMARY_INCOME, BANK_STATEMENT_CONNECT } = INCOME_VERIFICATION;

  const isBankVerification = currentState === IncomeVerificationState.BANK_STATEMENT_VERIFICATION;

  const applyVerificationCloseLogic = () => {
    if (!isFeedbackFlow || uploadVerificationStatus !== 'failed') {
      return;
    }
    const wasAlreadyFailed = verificationSessionFailureCount > 1;
    onVerificationFailed?.(wasAlreadyFailed);
    clearErrors(`${name}.documents` as FieldPath<IncomeVerificationType>);
  };

  const handleDialogToggle = () => {
    if (isDialogBusy) {
      return;
    }
    applyVerificationCloseLogic();
    onToggle();
  };

  const handleUploadClose = () => {
    applyVerificationCloseLogic();
    onToggle();
  };
  return (
    <ContentDialog
      title={isBankVerification ? BANK_STATEMENT_CONNECT : VERIFY_PRIMARY_INCOME}
      isOpen={isOpen}
      onToggle={handleDialogToggle}
      showCloseButton={!isDialogBusy}
      size="medium"
      css={{
        width: 'min(900px, 90vw)',
      }}
    >
      {isBankVerification ? (
        <Box
          className={css({
            width: '100%',
            height: '100vh',
            display: 'flex',
            padding: 0,
            margin: 0,
          })}
        >
          <Stack gap="xxlarge" width="full">
            <BankStatementVerification
              name={name}
              closeDialog={onToggle}
              setCurrentState={setCurrentState}
              setIsBankError={setIsBankError}
            />
          </Stack>
        </Box>
      ) : (
        <Box
          className={css({
            width: '98%',
            display: 'flex',
            alignSelf: 'center',
          })}
        >
          <Stack gap="xxlarge" width="full">
            {currentState === IncomeVerificationState.BANK_STATEMENT && isBankStatementEnabled ? (
              <BankStatements
                setCurrentState={setCurrentState}
                incomeType={name}
                financialProductId={financialProductId}
              />
            ) : null}

            {currentState === IncomeVerificationState.UPLOAD_DOCUMENTS || !isBankStatementEnabled ? (
              <UploadDocuments
                name={`${name}.documents`}
                setCurrentState={setCurrentState}
                closeDialog={handleUploadClose}
                isBankError={isBankError}
                isBankStatementEnabled={isBankStatementEnabled}
                incomeType={incomeType}
                onDialogBusyChange={setIsDialogBusy}
                onVerificationStatusChange={setUploadVerificationStatus}
              />
            ) : null}
          </Stack>
        </Box>
      )}
    </ContentDialog>
  );
}

export default IncomeVerificationOptions;
